/**
 * TICKET SLICE
 *
 * User ticket bookings / purchases state and API operations.
 * Uses the same middleware pattern as orderSlice.
 *
 * STATE:
 * - userBookings: list of booking objects from GET /fetch-user-bookings
 * - fetchUserBookingsLoading / Error / Success
 *
 * API:
 * - GET /fetch-user-bookings — authenticated user’s bookings (token in header)
 *
 * USAGE:
 *   import { fetchUserBookings, selectUserBookings, ... } from '../../redux/ticketSlice';
 *   dispatch(fetchUserBookings());
 *   const list = useSelector(selectUserBookings);
 */

import { createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from './middleware/apiMiddleware';

const initialState = {
    userBookings: [],
    fetchUserBookingsLoading: false,
    fetchUserBookingsError: null,
    fetchUserBookingsSuccess: false,
    cancelUserBookingLoading: false,
    cancelUserBookingError: null,
    cancelUserBookingSuccess: false,
    cancelUserBookingData: null,
};

const normalizeBookingsPayload = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload?.data != null && Array.isArray(payload.data)) return payload.data;
    if (payload?.bookings != null && Array.isArray(payload.bookings)) {
        return payload.bookings;
    }
    return [];
};

const ticketSlice = createSlice({
    name: 'ticket',
    initialState,
    reducers: {
        beginFetchUserBookingsLoader: (state) => {
            state.fetchUserBookingsLoading = true;
            state.fetchUserBookingsError = null;
            state.fetchUserBookingsSuccess = false;
        },
        setFetchUserBookingsData: (state, action) => {
            state.fetchUserBookingsLoading = false;
            state.fetchUserBookingsError = null;
            state.fetchUserBookingsSuccess = true;
            state.userBookings = normalizeBookingsPayload(action.payload);
        },
        fetchUserBookingsError: (state, action) => {
            state.fetchUserBookingsLoading = false;
            state.fetchUserBookingsError =
                action.payload?.errorMessage || 'Failed to fetch bookings';
            state.fetchUserBookingsSuccess = false;
        },
        resetFetchUserBookingsStatus: (state) => {
            state.fetchUserBookingsLoading = false;
            state.fetchUserBookingsError = null;
            state.fetchUserBookingsSuccess = false;
        },
        clearUserBookings: (state) => {
            state.userBookings = [];
        },
        beginCancelUserBookingLoader: (state) => {
            state.cancelUserBookingLoading = true;
            state.cancelUserBookingError = null;
            state.cancelUserBookingSuccess = false;
            state.cancelUserBookingData = null;
        },
        setCancelUserBookingData: (state, action) => {
            state.cancelUserBookingLoading = false;
            state.cancelUserBookingError = null;
            state.cancelUserBookingSuccess = true;
            state.cancelUserBookingData = action.payload;
        },
        cancelUserBookingError: (state, action) => {
            state.cancelUserBookingLoading = false;
            state.cancelUserBookingError = action.payload?.errorMessage || 'Failed to cancel booking';
            state.cancelUserBookingSuccess = false;
            state.cancelUserBookingData = null;
        },
        resetCancelUserBookingStatus: (state) => {
            state.cancelUserBookingLoading = false;
            state.cancelUserBookingError = null;
            state.cancelUserBookingSuccess = false;
            state.cancelUserBookingData = null;
        },
    },
});

export const {
    beginFetchUserBookingsLoader,
    setFetchUserBookingsData,
    fetchUserBookingsError,
    resetFetchUserBookingsStatus,
    clearUserBookings,
    beginCancelUserBookingLoader,
    setCancelUserBookingData,
    cancelUserBookingError,
    resetCancelUserBookingStatus
} = ticketSlice.actions;

/**
 * FETCH USER BOOKINGS
 *
 * Endpoint: GET /fetch-user-bookings
 * Auth: Required (Authorization header from localStorage)
 */
export const fetchUserBookings = () => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: '/my-tickets',
            method: 'GET',
            onStart: beginFetchUserBookingsLoader.type,
            onSuccess: setFetchUserBookingsData.type,
            onError: fetchUserBookingsError.type,
            showGlobalError: false,
        })
    );
};

/**
 * CANCEL USER BOOKING
 * 
 * Endpoint: POST /refund-payment
 * Auth: Required (Authorization header from localStorage)
 */
export const cancelUserBooking = (bookingData) => (dispatch) => {
    dispatch(apiCallBegan({
        url: '/refund-payment',
        method: 'POST',
        data: bookingData,
        onStart: beginCancelUserBookingLoader.type,
        onSuccess: setCancelUserBookingData.type,
        onError: cancelUserBookingError.type,
        showGlobalError: false,
    }));
};

export const selectUserBookings = (state) => state.ticket.userBookings;
export const selectFetchUserBookingsLoading = (state) =>
    state.ticket.fetchUserBookingsLoading;
export const selectFetchUserBookingsError = (state) =>
    state.ticket.fetchUserBookingsError;
export const selectFetchUserBookingsSuccess = (state) =>
    state.ticket.fetchUserBookingsSuccess;

export default ticketSlice.reducer;
